import AddTool, { AddToolContext } from './add-tool.js';
import CubbyView from '../../../views/cubby-view.js';
import SectionView from '../../../views/section-view.js';
import Signal from '../../../control-flow/signal.js';
import logging from '../../../logging.js';
import { Cubby, Group, ParametricPart, Section } from '../../../domain.js';
import { PI } from '../../../math/constants.js';
import { approx, approxGreaterThan, approxGteq, approxLessThan, approxLteq, round2slot } from '../../../measurement.js';
import { getParentSectionView } from '../../../controllers/section-controllers.js';
import { partElevationAscending } from '../../../controllers/parametric-part-controllers.js';

const log = logging('tools');

/**
 * Is the given global elevation, y, between to fixed shelves without anything
 * intervening between them?
 */
const canAcceptCubby = (section: Section, y: number) => {
    section.parametricParts.sort(partElevationAscending);

    let
        below: ParametricPart = null,
        above: ParametricPart = null;
    for(const part of section.parametricParts) {
        if(part.name.startsWith('Fixed')) {
            if(approx(part.position.y, y)) {
            } else if(approxGreaterThan(y, part.position.y)) {
                below = part;
            } else if(above == null && approxGreaterThan(part.position.y, y)) {
                above = part;
            }
        }
    }

    if(below == null || above == null) return false;

    for(const part of section.parametricParts) {
        if(part == above) break;
        if(part != below
           && approxGreaterThan(part.position.y, below.position.y)
           && approxLessThan(part.position.y, above.position.y))
       {
           return false;
       }
    }

    for(const asset of section.assets) {
        if(approxGteq(asset.position.y, below.position.y)
           && approxLteq(asset.position.y, above.position.y))
       {
           return false;
       }
    }

    for(const drawer of section.drawers) {
        if(approxGteq(drawer.position.y, below.position.y)
           && approxLteq(drawer.position.y, above.position.y))
       {
           return false;
       }
    }

    for(const cubby of section.cubbies) {
        if(approxGteq(cubby.position.y, below.position.y)
           && approxLteq(cubby.position.y, above.position.y))
       {
           return false;
       }
    }

    return true;
};

/**
 * Finds the global elevation of the fixed shelves above and below the given
 * elevation, y. This should only be called when canAcceptCubby has returned
 * true for the given section and value of y.
 */
const getCubbySpan = (section: Section, y: number): [y: number, height: number] => {
    let
        below: ParametricPart = null,
        above: ParametricPart = null;
    for(const part of section.parametricParts) {
        if(part.name.startsWith('Fixed')) {
            if(approx(part.position.y, y)) {
            } else if(approxGteq(y, part.position.y)) {
                below = part;
            } else if(above == null && approxGreaterThan(part.position.y, y)) {
                above = part;
            }
        }
    }

    return [below.position.y + 3/8,
            above.position.y - below.position.y - above.height];
};

export class AddCubbyTool extends AddTool
{

    /**
     * This is the section that the mouse is currently over.
     */
    target?: SectionView;

    model: Cubby;
    proxy: CubbyView;


    declare onSuccess: Signal<[Cubby]>;

    constructor(cubby: Cubby, elt: HTMLElement, ctx: AddToolContext) 
    {
        super(elt, ctx);

        this.model = Cubby.fromJSON({
            width: 12,
            height: 12,
            depth: 14,
            thickness: 0.5,
            countX: cubby.countX,
            countY: cubby.countY,
            diagonal: cubby.diagonal,
        });
        this.ctx.defaults.reset(this.model);
        this.model.commit();

        const group = Group.fromJSON({
            rotation: { x: PI/40, y: PI/40, z: 0 },
        });
        group.cubbies.push(this.model);
        group.commit(true);

        this.updateThumbnail(group);
    }

    dragenter(): void {
        log.debug('AddCubbyTool.dragenter()');

        this.proxy = new CubbyView(this.model);
    }

    dragover(event: PointerEvent, target: HTMLElement): void {
        log.debug('AddCubbyTool.dragover()');

        this.updateWorldPosition(event, target);
        this.onDisplayChange.trigger();

        const
            y = round2slot(this.worldPosition.y),
            hit = this.ctx.picker.topHit,
            sectionView = getParentSectionView(hit);
        if(hit?.userData.isSectionHit
           && canAcceptCubby(sectionView.model, y))
        {
            const
                proxy = this.proxy,
                model = this.model,
                target: SectionView = this.target = sectionView,
                [y1, height] = getCubbySpan(target.model, y);

            target.add(proxy);

            model.position.x = model.position.z = 0;
            model.position.y = y1;
            model.width = target.model.width;
            model.height = height;
            model.depth = target.model.depth;
        }
        else {
            delete this.target;

            // check if the proxy belongs to the scene ... if so, fine, else
            // remake the proxy and add it back to the scene.
            if(this.proxy.parent != this.ctx.scene) {
                this.proxy.removeFromParent();
                this.ctx.scene.add(this.proxy);
            }

            // regardless, set the position of the model/proxy
            this.model.width = 18;
            this.model.height = 24;
            this.model.position.x = this.worldPosition.x - 6;
            this.model.position.y = this.worldPosition.y - 6;
            this.model.position.z = 25;
        }
    }

    dragleave(): void {
        log.debug('AddCubbyTool.dragleave()');
        this.proxy?.removeFromParent();
        this.onDisplayChange.trigger();
    }

    cancel(): void {
        log.debug('AddCubbyTool.cancel()');
        this.proxy?.removeFromParent();

        // this will clean up any trash we may have left in the layouts
        //this.app.refresh();
        this.onDisplayChange.trigger();
    }

    end(): void {
        log.debug('AddCubbyTool.end()');
        this.proxy?.removeFromParent();

        // if we ended over a section, then we'll have a defined target
        // property, which means we can drop something.
        if(this.target) {
            this.model.commit();
            const model = this.model.clone(true);
            model.parent = this.target.model;
            this.target.model.cubbies.push(model);
            this.onSuccess.trigger(model);
        }

        this.onDisplayChange.trigger();
    }

}
export default AddCubbyTool;
